import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { getPreface } from "../lib/supabase";

const documents = [
  { id: "dedicace", label: "Dédicace" },
  { id: "avant_propos", label: "Avant-propos" },
  { id: "introduction", label: "Introduction" },
];

const DocumentPage = () => {
  const { section } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentIndex = documents.findIndex(d => d.id === section);
  const prevDoc = currentIndex > 0 ? documents[currentIndex - 1] : null;
  const nextDoc = currentIndex < documents.length - 1 ? documents[currentIndex + 1] : null;

  useEffect(() => {
    if (!section) return;
    setLoading(true);
    const data = getPreface(section);
    setContent(data);
    setLoading(false);
    window.scrollTo(0, 0);
  }, [section]);

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p className="font-body text-ink-secondary italic">Chargement...</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-ink-secondary mb-4">Document non trouvé</p>
          <Button asChild variant="outline">
            <Link to="/">Retour à l'accueil</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper animate-fade-in">
      <div className="reading-container py-12">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl text-ink-primary mb-3">
            {content.title}
          </h1>
          {content.subtitle && (
            <p className="font-body text-lg text-ink-secondary italic">
              {content.subtitle}
            </p>
          )}
          <div className="divider-ornate mt-8">
            <span className="font-decorative text-gold-leaf px-4">✦</span>
          </div>
        </header>

        {/* Content */}
        {content.html ? (
          <article
            className="doc-html font-body text-lg text-ink-primary leading-loose"
            data-testid="document-html-content"
            dangerouslySetInnerHTML={{ __html: content.html }}
          />
        ) : (
          <article className="font-body text-lg text-ink-primary leading-loose">
            {content.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-6 text-justify" data-testid={`paragraph-${index}`}>
                {paragraph}
              </p>
            ))}
          </article>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-16 pt-8 border-t border-border">
          {prevDoc ? (
            <Button
              asChild
              variant="ghost"
              className="text-ink-primary hover:text-rubric-red font-body"
              data-testid="prev-doc"
            >
              <Link to={`/documents/${prevDoc.id}`}>
                <ChevronLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {prevDoc.label}
              </Link>
            </Button>
          ) : (
            <div />
          )}
          
          {nextDoc ? (
            <Button
              asChild
              variant="ghost"
              className="text-ink-primary hover:text-rubric-red font-body"
              data-testid="next-doc"
            >
              <Link to={`/documents/${nextDoc.id}`}>
                {nextDoc.label}
                <ChevronRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
              </Link>
            </Button>
          ) : (
            <Button
              asChild
              variant="ghost"
              className="text-ink-primary hover:text-rubric-red font-body"
              data-testid="start-reading"
            >
              <Link to="/bible/genese/1">
                Commencer la lecture
                <ChevronRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPage;
